'use client';

import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  BookOpen,
  ChartNoAxesCombined,
  Layers,
  LogIn,
  Menu,
  MessageCircleQuestion,
  MessageSquare,
  Mic,
  ScanText,
  Settings,
  Sparkles,
  User,
  X
} from 'lucide-react';
import { SidebarItem, SidebarLogoutButton } from './sidebar-wrapper';
import { SignInButton } from './sign-in-button';
import { Session } from 'next-auth';
import { Button } from '@/src/components/ui/button';

interface SidebarClientProps {
  session: Session | null;
}

// Create a context to share the close menu function
export const SidebarContext = createContext<{ closeMenu: () => void }>({
  closeMenu: () => {}
});

export function SidebarClient({ session }: SidebarClientProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Create a memoized closeMenu function to pass to children
  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle clicks outside the sidebar to close it on mobile
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    }

    // Add event listener when sidebar is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeMenu]);

  return (
    <SidebarContext.Provider value={{ closeMenu }}>
      {/* Mobile menu button */}
      <Button
        ref={menuButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        variant='ghost'
        size='icon'
        className='fixed left-4 top-4 z-40 h-10 w-10 rounded-md bg-white shadow-sm md:hidden'
      >
        {isOpen ? (
          <X className='text-muted' size={20} />
        ) : (
          <Menu className='text-muted' size={20} />
        )}
      </Button>

      {/* Overlay to capture clicks outside sidebar on mobile */}
      {isOpen && (
        <div
          className='fixed inset-0 z-40 bg-black/20 md:hidden'
          onClick={closeMenu}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className={`fixed z-50 flex h-screen w-64 flex-col bg-sidebar shadow-md transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'} `}
      >
        {/* Logo */}
        <div className='flex items-center justify-between border-b border-border p-5'>
          <div className='flex items-center gap-2'>
            <Image
              src='/ukata.png'
              alt='UKATA Logo'
              width={40}
              height={40}
              className='rounded'
            />
            <span className='text-xl font-semibold'>UKATA</span>
          </div>

          {/* Close button for mobile */}
          <Button
            onClick={closeMenu}
            variant='ghost'
            size='icon'
            className='h-8 w-8 rounded-md md:hidden'
          >
            <X className='text-muted' size={18} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className='flex-1 overflow-y-auto px-3 py-6'>
          <div className='space-y-1'>
            <SidebarItem
              label='Dashboard'
              icon={<ChartNoAxesCombined size={20} />}
              href='/'
            />
            <SidebarItem label='Speaking' icon={<Mic size={20} />}>
              <Link href='/speaking/pronunciation'>Pronunciation</Link>
              <Link href='/speaking/simulated-talks'>Simulated Talks</Link>
              <Link href='/speaking/guided'>Guided Speech</Link>
              <Link href='/speaking/shadow-scripts'>Shadow Scripts</Link>
            </SidebarItem>
            <SidebarItem label='Quizzes' icon={<MessageCircleQuestion size={20} />}>
              <Link href='/quizzes/toeic'>TOEIC</Link>
              <Link href='/quizzes/past-attempts'>Past Attempts</Link>
            </SidebarItem>
            <SidebarItem label='Flashcards' icon={<Layers size={20} />}>
              <Link href='/flashcards'>My Decks</Link>
              <Link href='/flashcards/explore'>Explore</Link>
              <Link href='/flashcards/review'>SRS Review</Link>
            </SidebarItem>
            <SidebarItem label='Vocabulary' icon={<BookOpen size={20} />}>
              <Link href='/vocabulary'>Browse</Link>
              <Link href='/vocabulary/journal'>Journal</Link>
            </SidebarItem>
            <SidebarItem
              label='Courses'
              icon={<BookOpen size={20} />}
              href='/courses'
            />
            <SidebarItem
              label='My Lessons'
              icon={<Sparkles size={20} />}
              href='/lessons'
            />
            <SidebarItem label='Tools' icon={<ScanText size={20} />}>
              <Link href='/tools/text-analysis'>Text Analysis</Link>
              <Link href='/tools/dictionary'>Dictionary</Link>
              <Link href='/tools/listening'>Listening Sets</Link>
            </SidebarItem>
            <SidebarItem label='Chat' icon={<MessageSquare size={20} />}>
              <Link href='/chat/mirror'>Mirror Mode</Link>
            </SidebarItem>
            <SidebarItem
              label='Course Maker'
              icon={<Sparkles size={20} />}
              href='/maker'
            />
            <SidebarItem
              label='Settings'
              icon={<Settings size={20} />}
              href='/settings'
            />
            {/* <SidebarItem
              label="Listening"
              icon={<Headphones size={20} />}
              href="/listening"
            /> */}
            {/* <SidebarItem
              label="Reading"
              icon={<BookOpen size={20} />}
              href="/reading"
            />
            <SidebarItem
              label="Settings"
              icon={<Settings size={20} />}
              href="/settings"
            /> */}
          </div>
        </nav>

        {/* User Profile */}
        <div className='border-t border-border p-4'>
          {session ? (
            <Link
              href='/profile'
              className='sidebar-link rounded-md hover:bg-sidebar-hover'
            >
              <div className='flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-sidebar-hover'>
                {session.user?.image ? (
                  <Image
                    src={session.user?.image}
                    alt={`${session.user?.name || 'User'}'s Avatar`}
                    width={36}
                    height={36}
                    className='object-cover'
                  />
                ) : (
                  <User size={18} className='text-muted' />
                )}
              </div>
              <div>
                <div className='text-base font-medium'>
                  {session.user?.name || 'Guest User'}
                </div>
                <div className='text-xs text-muted-foreground'>
                  {session.user?.email || ''}
                </div>
              </div>
            </Link>
          ) : (
            <SignInButton className='sidebar-link w-full justify-center bg-primary text-primary-foreground hover:bg-primary/90'>
              <LogIn size={18} />
              <span>Sign In</span>
            </SignInButton>
          )}
        </div>

        {/* Help */}
        <div className='space-y-2 p-4'>
          <Link
            href='/public/contact'
            className='sidebar-link text-muted-foreground hover:text-foreground'
          >
            <span>Contact</span>
          </Link>
          <Link
            href='/public/privacy'
            className='sidebar-link text-muted-foreground hover:text-foreground'
          >
            <span>Privacy</span>
          </Link>
          <Link
            href='/public/terms'
            className='sidebar-link text-muted-foreground hover:text-foreground'
          >
            <span>Terms</span>
          </Link>
          {session && <SidebarLogoutButton />}
        </div>
      </div>
    </SidebarContext.Provider>
  );
}
